import { useRef, ChangeEvent, Dispatch, SetStateAction } from "react";
import { Avatar, Box, FormControl, FormLabel, IconButton, TextField } from "@mui/material";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage"; // Firebase storage imports
import { colors } from "../../../../../theme/palette";
import { useTranslation } from "react-i18next";
import { EditGroupMediaIcon, FallBackGroupIcon } from "../../../../../assets/icons/MUI/GroupsIcons";
import typography from "../../../../../theme/typography";
import AddMembersToGroup from "./AddMembersToGroup";
import { UserInfo } from "../../../../../types";
import ImageUploader from "../../../../../shared/ImageUploader/ImageUploader";
import { useImageUploader } from "../../../../../shared/ImageUploader/useImageUploader";

const formControlStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  rowGap: "6px",
  width: "100%",
};

const textFieldStyle = {
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "24px",
  color: colors.Gray400,
  width: "100%",
  "& .MuiInputBase-root": {
    border: `1px solid ${colors.Gray200}`,
    background: "#FFF",
  },
};

const GroupDataForm = ({
  formData,
  setFormData,
  handleChange,
  errors,
  membersList,
  setMembersList,
  handleRemoveUser,
  isImageUploading,
  handleUploadStart,
  handleUploadComplete,
}: {
  formData: {
    groupName: string;
    groupDescription: string;
    groupMedia?: string;
  };
  setFormData: Dispatch<
    SetStateAction<{
      groupMedia: string;
      groupName: string;
      groupDescription: string;
    }>
  >;
  handleChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  errors: {
    groupName: string;
    groupDescription: string;
  };
  membersList: UserInfo[];
  setMembersList: Dispatch<SetStateAction<UserInfo[]>>;
  handleRemoveUser: (user: UserInfo | null) => void;
  isImageUploading: boolean;
  handleUploadStart: () => void;
  handleUploadComplete: (file: File, url: string, fileName: string) => void;
}) => {
  const { t } = useTranslation();

  return (
    <Box
      component="form"
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        rowGap: "24px",
      }}
    >
      <ImageUploader
        imageSrc={formData.groupMedia}
        altText={FallBackGroupIcon}
        onUploadStart={handleUploadStart}
        onUploadComplete={handleUploadComplete}
        disabled={isImageUploading}
        avatarDim="76px"
      />
      <FormControl sx={formControlStyle}>
        <FormLabel
          sx={{
            textAlign: "start",
            ...typography.body14SemiBold,
            color: colors.Dark600,
          }}
        >
          {t("pages.entity.groups.groupName")}
        </FormLabel>
        <TextField
          name="groupName"
          value={formData.groupName}
          placeholder={t("pages.entity.groups.enterGroupName")}
          onChange={handleChange}
          sx={{
            ...textFieldStyle,
            "& .MuiInputBase-root": {
              borderRadius: "8px !important",
            },
          }}
          error={!!errors.groupName}
          helperText={errors.groupName}
          inputProps={{
            maxLength: 100,
            autoComplete: "new-password",
            form: {
              autoComplete: "off",
            },
          }}
        />
      </FormControl>

      <FormControl sx={formControlStyle}>
        <FormLabel
          sx={{
            textAlign: "start",
            ...typography.body14SemiBold,
            color: colors.Dark600,
          }}
        >
          {t("pages.entity.groups.groupDescription")}
        </FormLabel>
        <TextField
          placeholder={t("pages.entity.groups.writeSmallDescription")}
          multiline
          rows={4}
          name="groupDescription"
          value={formData.groupDescription}
          onChange={handleChange}
          sx={{
            ...textFieldStyle,
            "& .MuiInputBase-root": {
              borderRadius: "8px !important",
            },
          }}
          error={!!errors.groupDescription}
          helperText={errors.groupDescription || t("pages.entity.groups.limitedTo200Chars")}
          inputProps={{
            maxLength: 200,
            autoComplete: "new-password",
            form: {
              autoComplete: "off",
            },
          }}
        />
      </FormControl>

      <AddMembersToGroup
        membersList={membersList}
        setMembersList={setMembersList}
        handleRemoveUser={handleRemoveUser}
      />
    </Box>
  );
};

export default GroupDataForm;
